/// <reference types="google.maps" />
import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material'
import { Clear as ClearIcon } from '@mui/icons-material'
import { useGooglePlaces } from '../hooks/useGooglePlaces'
import { useDebounce } from '../hooks/useDebounce'

interface AddressComponents {
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  zipCode: string
  postalCode: string
  country: string
  latitude?: number
  longitude?: number
}

interface AddressAutocompleteProps {
  value: string
  onChange: (address: string, components?: AddressComponents) => void
  placeholder?: string
  label?: string
  required?: boolean
  error?: boolean
  helperText?: string
  disabled?: boolean
  sx?: any
  autoFocus?: boolean
  variant?: 'standard' | 'outlined' | 'filled'
  size?: 'small' | 'medium'
  fullWidth?: boolean
  addressType?: 'address' | 'billingAddress' // To distinguish address vs billing
  onAddressSelect?: (components: AddressComponents) => void
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Start typing an address...',
  label,
  required = false,
  error = false,
  helperText,
  disabled = false,
  sx,
  autoFocus = false,
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
  addressType = 'address',
  onAddressSelect,
}) => {
  const {
    isLoaded,
    isLoading,
    error: loadError,
    autocompleteService,
    placesService,
  } = useGooglePlaces()
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isSelecting, setIsSelecting] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [hasUserInteracted, setHasUserInteracted] = useState(false) // Track if user has interacted with input
  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const justSelectedRef = useRef(false) // Track if we just selected an address
  const lastSelectedValueRef = useRef<string>('') // Track the last selected address value
  const debouncedValue = useDebounce(value, 300) // Debounce for 300ms

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const getPlaceDetails = useCallback(
    (placeId: string) => {
      if (!placesService) return

      const request: google.maps.places.PlaceDetailsRequest = {
        placeId,
        fields: ['address_components', 'formatted_address', 'name', 'geometry'],
      }

      placesService.getDetails(request, async (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          setIsSelecting(true)
          const components: AddressComponents = {
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            zipCode: '',
            postalCode: '',
            country: 'US',
            latitude: undefined,
            longitude: undefined,
          }

          // Extract latitude and longitude from geometry
          if (place.geometry?.location) {
            components.latitude = place.geometry.location.lat()
            components.longitude = place.geometry.location.lng()
          }

          if (place.address_components) {
            let streetNumber = ''
            let route = ''
            let postalCodeSuffix = ''

            place.address_components.forEach(component => {
              const types = component.types

              if (types.includes('street_number')) {
                streetNumber = component.long_name
              }
              if (types.includes('route')) {
                route = component.long_name
              }
              if (types.includes('subpremise') || types.includes('premise')) {
                components.addressLine2 = component.long_name
              }
              // Handle city - try locality first, then sublocality, then administrative_area_level_2
              if (types.includes('locality')) {
                components.city = component.long_name
              } else if (!components.city && types.includes('sublocality')) {
                components.city = component.long_name
              } else if (
                !components.city &&
                types.includes('administrative_area_level_2')
              ) {
                components.city = component.long_name
              }
              // State - use long_name for full state name
              if (types.includes('administrative_area_level_1')) {
                components.state = component.long_name || component.short_name
              }
              // Postal code
              if (types.includes('postal_code')) {
                components.zipCode = component.long_name
                components.postalCode = component.long_name
              }
              if (types.includes('postal_code_suffix')) {
                postalCodeSuffix = component.long_name
              }
              // Country
              if (types.includes('country')) {
                components.country = component.short_name || component.long_name
              }
            })

            // Combine ZIP+4 if suffix is available
            if (components.zipCode && postalCodeSuffix) {
              const zipPlus4 = `${components.zipCode}-${postalCodeSuffix}`
              components.zipCode = zipPlus4
              components.postalCode = zipPlus4
            }

            // Combine street number and route to form address line 1
            if (streetNumber && route) {
              components.addressLine1 = `${streetNumber} ${route}`.trim()
            } else if (route) {
              components.addressLine1 = route
            } else if (streetNumber) {
              components.addressLine1 = streetNumber
            } else {
              // Fallback to formatted address first line if no street components
              const formattedParts = place.formatted_address?.split(',') || []
              components.addressLine1 = formattedParts[0]?.trim() || ''
            }
          }

          // Use addressLine1 (street address) instead of full formatted address
          const streetAddress =
            components.addressLine1 ||
            place.formatted_address?.split(',')[0]?.trim() ||
            value

          // Fallback: if ZIP is missing but we have lat/lng, reverse geocode to get postal code
          const fillZipFromLatLng = async () => {
            if (components.zipCode) return
            if (components.latitude === undefined || components.longitude === undefined) return
            if (!window.google?.maps?.Geocoder) return

            try {
              const geocoder = new window.google.maps.Geocoder()
              const results = await new Promise<google.maps.GeocoderResult[]>(
                resolve => {
                  geocoder.geocode(
                    {
                      location: {
                        lat: components.latitude!,
                        lng: components.longitude!,
                      },
                    },
                    (res, status) => {
                      if (
                        status === window.google.maps.GeocoderStatus.OK &&
                        Array.isArray(res)
                      ) {
                        resolve(res)
                      } else {
                        resolve([])
                      }
                    }
                  )
                }
              )

              const first = results?.[0]
              const addrComponents = first?.address_components || []
              let zip = ''
              let suffix = ''
              for (const c of addrComponents) {
                if (c.types.includes('postal_code')) zip = c.long_name
                if (c.types.includes('postal_code_suffix')) suffix = c.long_name
              }
              if (zip) {
                const combined = suffix ? `${zip}-${suffix}` : zip
                components.zipCode = combined
                components.postalCode = combined
              }
            } catch (e) {
              // ignore fallback errors
            }
          }

          await fillZipFromLatLng()

          // Store the selected value to prevent menu from reopening
          lastSelectedValueRef.current = streetAddress
          onChange(streetAddress, components)

          if (onAddressSelect) {
            onAddressSelect(components)
          }

          setShowSuggestions(false)
          setIsSelecting(false)
          // Clear predictions and mark as just selected to prevent menu from reopening
          setPredictions([])
          justSelectedRef.current = true
          // Keep the flag true for a bit longer to prevent immediate reopening
          setTimeout(() => {
            justSelectedRef.current = false
            // Clear the last selected value after a delay
            setTimeout(() => {
              lastSelectedValueRef.current = ''
            }, 2000)
          }, 1000)
        }
      })
    },
    [placesService, value, onChange, onAddressSelect]
  )

  // Handle autocomplete suggestions with debouncing
  useEffect(() => {
    // Don't show suggestions if disabled
    if (disabled) {
      setShowSuggestions(false)
      setPredictions([])
      setHasUserInteracted(false)
      return
    }

    // Only show suggestions if user has interacted with the input
    if (!hasUserInteracted) {
      setShowSuggestions(false)
      setPredictions([])
      return
    }

    // Don't show suggestions if we just selected an address
    if (justSelectedRef.current) {
      setShowSuggestions(false)
      setPredictions([])
      // Reset the flag after a short delay to allow for value updates
      const timer = setTimeout(() => {
        justSelectedRef.current = false
      }, 500)
      return () => clearTimeout(timer)
    }

    // Don't show suggestions if we're in the middle of selecting an address
    if (isSelecting || !isLoaded || !autocompleteService) {
      setShowSuggestions(false)
      return
    }

    const inputValue = debouncedValue.trim()

    // Don't show suggestions if this matches the last selected value (prevents reopening)
    if (
      inputValue === lastSelectedValueRef.current &&
      lastSelectedValueRef.current
    ) {
      setShowSuggestions(false)
      setPredictions([])
      return
    }

    if (inputValue.length < 3) {
      setPredictions([])
      setShowSuggestions(false)
      setSelectedIndex(-1)
      return
    }

    const request = {
      input: inputValue,
      componentRestrictions: { country: 'us' },
      types: ['address'],
    } as google.maps.places.AutocompleteRequest

    autocompleteService.getPlacePredictions(request, (predictions, status) => {
      // Don't show suggestions if we just selected or are selecting
      if (justSelectedRef.current || isSelecting) {
        return
      }

      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        predictions
      ) {
        setPredictions(predictions)
        setShowSuggestions(true)
        setSelectedIndex(-1)
      } else {
        setPredictions([])
        setShowSuggestions(false)
        setSelectedIndex(-1)
      }
    })
  }, [
    debouncedValue,
    isLoaded,
    autocompleteService,
    isSelecting,
    hasUserInteracted,
    disabled,
  ])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    setHasUserInteracted(true) // Mark that user has interacted
    onChange(inputValue)
    setSelectedIndex(-1)
  }

  const handleSelectPrediction = useCallback(
    (prediction: google.maps.places.AutocompletePrediction) => {
      if (prediction.place_id) {
        setIsSelecting(true)
        setShowSuggestions(false)
        setPredictions([])
        justSelectedRef.current = true
        getPlaceDetails(prediction.place_id)
      }
    },
    [getPlaceDetails]
  )

  const handleClear = useCallback(() => {
    // Clear only the address field, keep city, state, zip unchanged
    onChange('', {
      addressLine1: '',
      addressLine2: '',
      city: '', // Pass empty but don't clear parent's city field
      state: '', // Pass empty but don't clear parent's state field
      zipCode: '', // Pass empty but don't clear parent's zip field
      postalCode: '',
      country: '',
      latitude: undefined,
      longitude: undefined,
    })
    setShowSuggestions(false)
    setPredictions([])
    setSelectedIndex(-1)
    justSelectedRef.current = false
    lastSelectedValueRef.current = '' // Clear the tracked selected value
    setIsSelecting(false)
    setHasUserInteracted(false) // Reset interaction flag on clear
    inputRef.current?.focus()
  }, [onChange])

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showSuggestions || predictions.length === 0) {
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex(prev =>
            prev < predictions.length - 1 ? prev + 1 : prev
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1))
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && selectedIndex < predictions.length) {
            handleSelectPrediction(predictions[selectedIndex])
          } else if (predictions.length > 0) {
            handleSelectPrediction(predictions[0])
          }
          break
        case 'Escape':
          setShowSuggestions(false)
          setSelectedIndex(-1)
          break
      }
    },
    [showSuggestions, predictions, selectedIndex, handleSelectPrediction]
  )

  return (
    <Box
      sx={{
        position: 'relative',
        width: fullWidth ? '100%' : 'auto',
        overflow: 'visible',
        ...sx,
      }}
    >
      <TextField
        inputRef={inputRef}
        fullWidth={fullWidth}
        value={value}
        onChange={handleInputChange}
        placeholder={isLoading ? 'Loading address suggestions...' : placeholder}
        label={label}
        required={required}
        error={error || !!loadError}
        helperText={helperText || loadError}
        disabled={disabled || isLoading}
        variant={variant}
        size={size}
        autoFocus={autoFocus}
        onFocus={() => {
          // Only mark as interacted and show suggestions if user has already typed something
          // This prevents showing suggestions when focusing on pre-populated values
          if (
            hasUserInteracted &&
            predictions.length > 0 &&
            !justSelectedRef.current &&
            !disabled
          ) {
            setShowSuggestions(true)
          }
        }}
        onBlur={() => {
          // Close suggestions when field loses focus
          // Use a small delay to allow click events on suggestions to fire first
          setTimeout(() => {
            if (!suggestionsRef.current?.contains(document.activeElement)) {
              setShowSuggestions(false)
              setHasUserInteracted(false)
            }
          }, 200)
        }}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment:
            value && !disabled ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClear}
                  edge="end"
                  size="small"
                  sx={{
                    color: 'grey.500',
                    '&:hover': {
                      color: 'grey.700',
                    },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: 'white',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '& fieldset': {
              borderColor: 'grey.300',
            },
            '&:hover fieldset': {
              borderColor: 'grey.400',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2E7DAF',
              borderWidth: 2,
            },
          },
          '& .MuiInputBase-input': {
            py: size === 'small' ? 1 : 1.5,
            fontSize: '0.875rem',
          },
          ...sx,
        }}
      />
      {showSuggestions && predictions.length > 0 && (
        <Box
          ref={suggestionsRef}
          onMouseDown={(e) => e.preventDefault()}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 9999,
            mt: 0.5,
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            maxHeight: '300px',
            overflowY: 'auto',
            overflowX: 'hidden',
            border: '1px solid #e0e0e0',
            // Custom scrollbar styling for better visibility
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
              '&:hover': {
                background: '#555',
              },
            },
            // Firefox scrollbar styling
            scrollbarWidth: 'thin',
            scrollbarColor: '#888 #f1f1f1',
          }}
        >
          {predictions.map((prediction, index) => (
            <Box
              key={prediction.place_id}
              onClick={() => handleSelectPrediction(prediction)}
              onMouseEnter={() => setSelectedIndex(index)}
              sx={{
                p: 1.5,
                cursor: 'pointer',
                borderBottom:
                  index < predictions.length - 1 ? '1px solid #f0f0f0' : 'none',
                bgcolor: selectedIndex === index ? '#f5f5f5' : 'transparent',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                },
                '&:first-of-type': {
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                },
                '&:last-of-type': {
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'grey.900',
                  mb: 0.5,
                }}
              >
                {prediction.structured_formatting.main_text}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'grey.600',
                  fontSize: '0.75rem',
                  display: 'block',
                }}
              >
                {prediction.structured_formatting.secondary_text}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
